import { useEffect, useRef, type ReactNode } from 'react';
import i18n from '../i18n';
import { useAccountInfo } from '../api/queryHooks/account';
import { useUserData, useSetUserLocale } from '../api/queryHooks/user';
import { useEnabledLocales } from '../hooks/useEnabledLocales';
import { useLogin } from '../hooks/useLogin';
import { isLocale } from './locales';
import {
  readCachedShopDefault,
  readChoice,
  resolveLocale,
  writeCachedShopDefault,
  writeChoice,
} from './localePreference';

/**
 * Reconciles the pre-paint language (localePreference.bootLocale) with the
 * live values: the shop's default from /accounts/info and the user's stored
 * preference from /users/me. Mirrors theme/ThemeProvider's shape.
 *
 * Talks to the app's i18next singleton directly (../i18n) — the same pattern
 * apiClient.ts uses — rather than react-i18next's useTranslation(). That
 * hook's `i18n` comes from React context and would silently be a *different*
 * instance from the app singleton whenever an ancestor wraps the tree in its
 * own I18nextProvider (every test using the shared renderWithProviders does,
 * with an isolated per-test instance). In production there is exactly one
 * i18next instance (no I18nextProvider outside test-utils), so this doesn't
 * change behavior there — it's still the more correct choice for an app-root
 * reconciler. Note this component DOES still re-render when the language
 * changes, just not because of this hook: useAccountInfo() (below) calls
 * useTranslation() internally to key its query on i18n.language, so a
 * changeLanguage call here still ripples back through that query.
 *
 * The absence of a server preference NEVER downgrades an explicit local
 * choice — that is what keeps the language after logout, when /users/me stops
 * returning a row entirely.
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const { data: account } = useAccountInfo();
  const { data: user } = useUserData();
  const { impersonation } = useLogin();
  const enabled = useEnabledLocales();
  const setUserLocale = useSetUserLocale();

  const shopDefault = isLocale(account?.default_locale) ? account.default_locale : null;
  // While impersonating, /users/me returns the CUSTOMER's row, including
  // their stored locale — treat that server preference as absent so it never
  // wins reconciliation for the admin's own UI, and never gets mirrored into
  // the admin's own f2m_locale (see the `writeChoice` call below). The admin
  // keeps whatever they already had resolved (their local choice, or the
  // shop default), matching the spec's promise that impersonation leaves the
  // admin's own language alone and never touches the customer's stored
  // preference either (the server already no-ops that write; see
  // users_controller.rb#update_locale).
  const userLocale = impersonation ? null : (user?.locale ?? null);

  useEffect(() => {
    if (shopDefault) writeCachedShopDefault(shopDefault);
  }, [shopDefault]);

  // Mirrors the resolved language onto <html lang> — screen readers, browser
  // translate prompts, hyphenation/spellcheck and search-engine language
  // detection all key off this attribute, so it must track whatever language
  // is actually rendered, not only changes driven by server data. A purely
  // local pick (LanguageSelector, for a visitor with no session, or with a
  // session whose row-locale doesn't change) calls i18n.changeLanguage()
  // directly without touching userLocale/shopDefault/enabled, so those three
  // alone under-fire; `i18n.resolvedLanguage` is added below so ANY change to
  // the actually-rendered language — regardless of who triggered it —
  // re-runs this effect too.
  //
  // Termination: `target` here is a function only of userLocale/choice/
  // shopDefault/enabled, never of resolvedLanguage itself, so adding
  // resolvedLanguage as a dependency can't let this effect's own writes
  // change its own target. Given fixed userLocale/shopDefault/enabled, every
  // pass computes the same target. Whichever pass first sees
  // resolvedLanguage !== target calls i18n.changeLanguage(target); once that
  // lands (whether this effect changed it or an external actor like
  // LanguageSelector did), the NEXT pass sees resolvedLanguage === target,
  // the changeLanguage guard is false, and the pass is a no-op with respect
  // to i18n state — no further language change fires, so no further
  // re-render is triggered by this effect. It settles within one extra pass
  // per genuine target change, not an unbounded loop.
  useEffect(() => {
    const target = resolveLocale({
      userLocale,
      choice: readChoice(),
      shopDefault: shopDefault ?? readCachedShopDefault(),
      enabled,
    });
    if (i18n.resolvedLanguage !== target) void i18n.changeLanguage(target);
    document.documentElement.lang = target;
    // A server preference that actually won becomes this device's choice too,
    // so it outlives the session.
    if (userLocale === target && readChoice() !== target) writeChoice(target);
  }, [userLocale, shopDefault, enabled, i18n.resolvedLanguage]);

  // A visitor who chose a language before having a row (anonymous, or a guest
  // created later at album-code redemption) gets that choice pushed up the
  // first time a row appears without one. Keyed on user.id (not a bare
  // per-mount boolean) so logging out and into a *different* account with no
  // stored locale — without a page reload — still gets its choice pushed;
  // a bare boolean would stay latched true from the previous user's push.
  const pushed = useRef<string | null>(null);
  useEffect(() => {
    // Also skip while impersonating: the server already no-ops this write
    // (Current.impersonator present), so pushing here would just be a
    // pointless request — and it would mean sending the ADMIN's own local
    // choice up as if it were a preference for the CUSTOMER's row.
    if (!user || user.locale || pushed.current === user.id || impersonation) return;
    const choice = readChoice();
    if (!choice) return;
    pushed.current = user.id;
    setUserLocale.mutate(choice);
  }, [user, setUserLocale, impersonation]);

  return <>{children}</>;
}
